import type { QueryClient } from '@tanstack/react-query';
import { OMNICHANNEL_CONVERSATIONS_QUERY_KEY } from '@/api/queries/omnichannel/key';
import { realtimeEventToMessage } from '@/lib/omnichannel/append-message-to-cache';
import type {
  OmnichannelConversation,
  OmnichannelConversationItem,
  OmnichannelNewMessageEvent,
  PaginatedConversationsResponse,
} from '@/types';

function isConversationItem(
  conversation: OmnichannelConversation
): conversation is OmnichannelConversationItem {
  return 'participant' in conversation && conversation.participant != null;
}

function sameConversation(id: string | number | undefined, target: string) {
  return id != null && String(id) === target;
}

/**
 * Updates sidebar previews immediately when a realtime message arrives,
 * including conversations that are not currently open.
 */
export function applyIncomingMessageToConversationsCache(
  queryClient: QueryClient,
  event: OmnichannelNewMessageEvent,
  options?: { isOpen?: boolean; classification?: string }
) {
  const conversationId = String(event.conversationId);
  const createdAt = event.message.createdAt ?? new Date().toISOString();
  const preview = event.message.content?.trim() || undefined;
  const channel = event.channel.toLowerCase();

  queryClient.setQueriesData<PaginatedConversationsResponse>(
    {
      queryKey: [OMNICHANNEL_CONVERSATIONS_QUERY_KEY],
      predicate: (query) => {
        const queryChannel = query.queryKey[1];
        return (
          typeof queryChannel !== 'string' ||
          queryChannel.toLowerCase() === channel
        );
      },
    },
    (current) => {
      if (!current?.data?.length) return current;

      let changed = false;
      const data = current.data.map((conversation) => {
        if (
          !conversation ||
          !sameConversation(conversation.id, conversationId)
        ) {
          return conversation;
        }

        changed = true;

        if (isConversationItem(conversation)) {
          const alreadyApplied =
            conversation.lastMessage?.timestamp === createdAt &&
            (preview
              ? conversation.lastMessage?.message === preview
              : conversation.lastMessage?.hasAttachment === true);

          return {
            ...conversation,
            ...(options?.classification
              ? { classification: options.classification }
              : {}),
            unreadCount:
              options?.isOpen || alreadyApplied
                ? conversation.unreadCount
                : (conversation.unreadCount ?? 0) + 1,
            lastMessage: {
              ...conversation.lastMessage,
              timestamp: createdAt,
              ...(preview ? { message: preview } : { hasAttachment: true }),
            },
          };
        }

        const incoming = realtimeEventToMessage(event);
        const existing = conversation.messages?.data ?? [];
        if (existing.some((message) => message.id === incoming.id)) {
          return conversation;
        }

        return {
          ...conversation,
          updated_time: createdAt,
          messages: {
            ...conversation.messages,
            data: [...existing, incoming],
          },
        };
      });

      return changed ? { ...current, data } : current;
    }
  );
}
